import React, { useCallback, useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import DailyIframe from '@daily-co/daily-js'

import { WaitingRoomSection } from 'components/WaitingRoomSection'
import { CallInfo } from 'components/CallInfo'
import { Button } from 'components/ui/Button'

import { callApi } from 'api'
import { useFetch } from 'hooks'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import { PROMISE_STATES } from 'utils/constants'
import { AlertMessage } from 'components/AlertMessage'
import { LoadingBarBlock } from 'components/LoadingBarBlock'
import useFetchWithRetry from 'hooks/useFetchWithRetry'

const getStoredTranscription = () => {
  const stored = window.localStorage.getItem('transcription')
  if (!stored) return []
  try {
    const parsed = JSON.parse(stored)
    return Array.isArray(parsed) ? parsed : []
  } catch (err) {
    return []
  }
}

const applyStylesForActiveCall = () => {
  const headerdisplay = document.querySelector('header')
  const basedisplay = document.querySelector('base')
  const chatdisplay = document.querySelector('section')
  const timerdisplay = document.querySelector('talk_start_in')
  const joincalldisplay = document.querySelector('btn_v7_upper')

  headerdisplay?.classList.add('remove')
  basedisplay?.classList.add('no_header_mod')
  chatdisplay?.classList.add('active_call_width')
  timerdisplay?.classList.add('active_call_margin')
  joincalldisplay?.classList.add('remove')
}

const createFrame = (videoContainer, userName) => {
  return DailyIframe.createFrame(videoContainer, {
    iframeStyle: {
      position: 'fixed',
      left: '0',
      bottom: '0',
      width: '100%',
      height: '100%',
    },
    showLeaveButton: true,
    showFullscreenButton: true,
    userName,
  })
}

const Huddle = (props) => {
  const history = useHistory()
  const [isEnding, setIsEnding] = useState(false)
  const [isCallLive, setCallLive] = useState(false)
  const [transcription, setTranscription] = useState(getStoredTranscription())
  const transcriptionRef = useRef(transcription)
  const videoRef = useRef(null)
  const callFrameRef = useRef(null)

  const { slug } = useParams()

  const {
    loading: activeCallLoading,
    error: activeCallError,
    data: activeCall,
    statusCode,
  } = useFetch(() => callApi.getActiveInstantCall(slug))

  const { data: retryData } = useFetchWithRetry(
    () => callApi.getActiveInstantCall(slug),
    'join_call_url'
  )
  const joinCallUrl =
    retryData?.result?.join_call_url || activeCall?.result?.join_call_url

  const currentUser = props.user?.user || props.user

  const isCallOwner = useCallback(
    () => props.isPro && currentUser?.channel_name === slug,
    [props.isPro, currentUser?.channel_name, slug]
  )

  const redirectToSummary = useCallback(() => {
    const redirectUrl = `/summary/group/${activeCall?.result?.id}`

    history.push(redirectUrl)
  }, [activeCall?.result?.id, history])

  const onLeftMeeting = useCallback(
    async (callId) => {
      const serializedTranscription = JSON.stringify(transcriptionRef.current)
      return callApi
        .endGroupCall({
          id: callId,
          transcription: serializedTranscription,
          transcript: serializedTranscription,
        })
        .then((res) => {
          window.localStorage.removeItem('transcription')
          redirectToSummary(callId)
        })
    },
    [redirectToSummary]
  )

  const onAppMessage = (callFrame, msg) => {
    const data = msg.data
    if ((msg?.fromId === 'transcription' || data?.text) && data?.is_final && data?.text) {
      const local = callFrame.participants().local
      const participant = callFrame.participants()[data.session_id]
      const userName =
        local.session_id === data.session_id
          ? local.user_name
          : participant?.user_name || 'Unknown Speaker'

      setTranscription((prevTranscription) => [
        ...prevTranscription,
        {
          name: userName,
          date: data?.timestamp,
          text: data?.text,
        },
      ])
    }
  }

  const startCall = useCallback(() => {
    if (callFrameRef.current) return

    const searchParams = new URLSearchParams(
      new URL(activeCall?.result?.join_call_url).search
    )
    const params = Object.fromEntries(searchParams.entries())
    callFrameRef.current = createFrame(videoRef.current, currentUser?.name)
    callFrameRef.current.on('left-meeting', async (event) => {
      setIsEnding(true)
      setCallLive(false)

      if (isCallOwner()) {
        await onLeftMeeting(activeCall?.result?.id)
      } else {
        if (props?.user) {
          redirectToSummary(activeCall?.result?.id)
        }
      }
    })
    callFrameRef.current.join({ url: activeCall?.result?.join_call_url, token: params.t })
    callFrameRef.current.on('joined-meeting', () => {
      Promise.resolve(callFrameRef.current.startTranscription()).catch(() => {})
    })
    callFrameRef.current.on('app-message', (msg) => {
      if (isCallOwner()) {
        onAppMessage(callFrameRef.current, msg)
      }
    })
  }, [
    activeCall?.result?.id,
    activeCall?.result?.join_call_url,
    currentUser?.name,
    isCallOwner,
    onLeftMeeting,
    props?.user,
    redirectToSummary,
  ])

  useEffect(() => {
    transcriptionRef.current = transcription
    window.localStorage.setItem('transcription', JSON.stringify(transcription))
  }, [transcription])

  useEffect(() => {
    setTranscription(getStoredTranscription())
    document.title = `Meeting room | Talkpro`
  }, [])

  useEffect(() => {
    if (
      activeCall?.result?.join_call_url &&
      isCallOwner() &&
      activeCallLoading === PROMISE_STATES.fulfilled &&
      props?.loading === PROMISE_STATES.fulfilled
    ) {
      setCallLive(true)
      applyStylesForActiveCall()
    }
  }, [activeCall, activeCallLoading, isCallOwner, props?.loading])

  useEffect(() => {
    if (
      activeCall?.result?.join_call_url &&
      isCallOwner() &&
      activeCallLoading === PROMISE_STATES.fulfilled &&
      isCallLive === true
    ) {
      startCall()
    }
  }, [
    activeCall?.result?.join_call_url,
    activeCallLoading,
    isCallLive,
    isCallOwner,
    startCall,
  ])

  // Channel not found
  if (activeCallLoading === PROMISE_STATES.rejected && statusCode === 404) {
    return (
      <WaitingRoomSection variant="huddle" withoutLogout>
        <AlertMessage message={activeCallError} />
      </WaitingRoomSection>
    )
  }

  // FIXME: refactor setTimeout logic
  const enterRoom = () => {
    setCallLive(true)
    setTimeout(() => {
      applyStylesForActiveCall()
      startCall()
    }, 3000)
  }

  const getInTouch = () => {
    window.location.href = `mailto:${activeCall?.result?.call_owner_email}`
  }

  // FIXME: refactor

  const callContent = `Welcome to ${activeCall?.result?.call_owner}'s private room. TalkPro is a smart, encrypted video call platform that will automatically summarize your conversation.`

  return (
    <WaitingRoomSection variant="huddle" withoutLogout>
      {!isCallLive ? (
        <>
          {activeCallLoading === PROMISE_STATES.fulfilled &&
            isCallOwner() &&
            activeCall?.result?.status === 202 && (
              <AlertMessage message="You haven't created any call yet" />
            )}

          {activeCallLoading === PROMISE_STATES.fulfilled &&
            !isCallOwner() &&
            !joinCallUrl && (
              <CallInfo variant="secondary">
                <CallInfo.Title>{`${activeCall?.result?.call_owner} meeting room is closed`}</CallInfo.Title>
                <CallInfo.Content>{callContent}</CallInfo.Content>
                <Button variant="outlined" size="md" rounded="sm" onClick={getInTouch}>
                  Get in touch
                </Button>
              </CallInfo>
            )}

          {activeCallLoading === PROMISE_STATES.fulfilled &&
            !isCallOwner() &&
            activeCall?.result?.status !== 202 &&
            joinCallUrl && (
              <CallInfo variant="secondary">
                <CallInfo.Title>{`${activeCall?.result?.call_owner} is waiting for you`}</CallInfo.Title>
                <CallInfo.Content>{callContent}</CallInfo.Content>
                <Button variant="success" size="md" rounded="sm" onClick={enterRoom}>
                  Enter Room
                </Button>
              </CallInfo>
            )}
          {isEnding && isCallOwner() && (
            <LoadingBarBlock title="Meeting notes are being generated" />
          )}
        </>
      ) : (
        <div id="daily_video" ref={videoRef} />
      )}
    </WaitingRoomSection>
  )
}

const mapStateToProps = ({ auth: { error, user, isPro, loading } }) => ({
  authError: error,
  user,
  isPro,
  loading,
})

export default connect(mapStateToProps)(Huddle)
